import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { LiveEditorFieldLockService } from './live-editor-field-lock.service';

/**
 * UI for fields dominated by another live editor.
 * Disable is owned by LiveEditorFieldLockService; this only shows the hint.
 *
 * `formControlName="title" appLiveEditorField`
 * `[formControl]="..." appLiveEditorField="title"`
 */
@Directive({ selector: '[appLiveEditorField]' })
export class LiveEditorDominatedFieldDirective implements OnInit, OnDestroy {
    @Input('appLiveEditorField') fieldPath = '';

    private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly fieldLock = inject(LiveEditorFieldLockService);

    private sub: Subscription | null = null;
    private hintEl: HTMLElement | null = null;
    private formFieldEl: HTMLElement | null = null;
    private path: string | null = null;

    ngOnInit(): void {
        this.path =
            this.fieldPath ||
            this.fieldLock.resolvePathFromElement(this.host.nativeElement);
        if (!this.path) {
            return;
        }

        this.formFieldEl =
            this.host.nativeElement.closest('.mat-mdc-form-field, .mat-form-field, mat-form-field') ?? null;

        this.sub = this.fieldLock.otherEditorsLocks$.subscribe(() => this.sync());
        this.sync();
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
        this.clearUi();
    }

    private sync(): void {
        if (!this.path || !this.fieldLock.isActive()) {
            this.clearUi();
            return;
        }

        const hint = this.fieldLock.getDominatedHint(this.path);
        if (!hint) {
            this.clearUi();
            return;
        }

        const el = this.host.nativeElement;
        this.renderer.setAttribute(el, 'title', hint);
        this.renderer.addClass(el, 'live-editor-dominated');
        this.formFieldEl?.classList.add('live-editor-dominated');
        this.renderHint(hint);
    }

    private renderHint(hint: string): void {
        const anchor = this.formFieldEl ?? this.host.nativeElement;
        if (!this.hintEl) {
            this.hintEl = this.renderer.createElement('div') as HTMLElement;
            this.renderer.addClass(this.hintEl, 'live-editor-dominated-hint');
            this.renderer.setAttribute(this.hintEl, 'role', 'status');
            this.renderer.appendChild(anchor, this.hintEl);
        }

        while (this.hintEl.firstChild) {
            this.renderer.removeChild(this.hintEl, this.hintEl.firstChild);
        }

        const icon = this.renderer.createElement('span') as HTMLElement;
        this.renderer.addClass(icon, 'material-icons');
        this.renderer.addClass(icon, 'live-editor-dominated-hint__icon');
        this.renderer.setProperty(icon, 'textContent', 'lock');
        this.renderer.appendChild(this.hintEl, icon);

        const text = this.renderer.createElement('span') as HTMLElement;
        this.renderer.setProperty(text, 'textContent', hint);
        this.renderer.appendChild(this.hintEl, text);
    }

    private clearUi(): void {
        const el = this.host.nativeElement;
        this.renderer.removeAttribute(el, 'title');
        el.classList.remove('live-editor-dominated');
        this.formFieldEl?.classList.remove('live-editor-dominated');
        if (this.hintEl?.parentNode) {
            this.renderer.removeChild(this.hintEl.parentNode, this.hintEl);
        }
        this.hintEl = null;
    }
}
